/**
 * The loyalty settings the platform reads, each with how its getter parses the value. Mirrors the server's
 * LoyaltySettings (Core/server/Libraries/BizWeb.NC.Core/Services/LoyaltySettings.cs), which refuses the same values:
 * these keep their names, cannot be deleted, and take only a value the getter reads (anything else was read as 0/off).
 */
export type LoyaltySettingKind = 'onOff' | 'wholeNumber' | 'amount';

const KINDS: Record<string, LoyaltySettingKind> = {
	loyaltypoints_active: 'onOff',
	loyaltypoints_register: 'onOff',
	loyaltypoints_birthday: 'onOff',
	loyaltypoints_gender: 'onOff',
	loyaltypoints_newappointment: 'onOff',
	loyaltypoints_expirynotifier: 'onOff',
	loyaltypoints_readblog: 'onOff',
	loyaltypoints_viewvideo: 'onOff',
	loyaltypoints_referafriend: 'onOff',
	loyaltypoints_review: 'onOff',
	loyaltypoints_expirationdate: 'wholeNumber',
	loyaltypoints_expirydaysnotifier: 'wholeNumber',
	loyaltypoints_redeemsteppoints: 'wholeNumber',
	loyaltypoints_ratio: 'amount',
	loyaltypoints_discountratio: 'amount',
	loyaltypoints_redeemstepamount: 'amount',
	loyaltypoints_minorderremainder: 'amount',
	loyaltypoints_referafriendminordervalue: 'amount'
};

const PATTERNS: Record<LoyaltySettingKind, RegExp> = {
	onOff: /^(0|1|true|false)$/i,
	wholeNumber: /^\d{1,9}$/,
	// The getters accept "." or "," as the decimal separator.
	amount: /^\d{1,12}([.,]\d{1,6})?$/
};

export function loyaltySettingKind(name: string | null | undefined): LoyaltySettingKind | undefined {
	return name ? KINDS[name.trim().toLowerCase()] : undefined;
}

/**
 * The "new appointment" points switch belongs to the optional Appointments module (BW-8453): the loyalty screens
 * leave it out while the module is off.
 */
export function isAppointmentsLoyaltySetting(name: string | null | undefined): boolean {
	return name?.trim().toLowerCase() === 'loyaltypoints_newappointment';
}

/** A platform loyalty setting: its name is fixed and it cannot be deleted. */
export function isKnownLoyaltySetting(name: string | null | undefined): boolean {
	return loyaltySettingKind(name) != null;
}

/** Whether the value suits the setting; a setting that is not a loyalty one takes any value. */
export function isValidLoyaltySettingValue(name: string | null | undefined, value: string | null | undefined): boolean {
	const kind = loyaltySettingKind(name);
	return kind == null || PATTERNS[kind].test((value ?? '').trim());
}
